import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Typography, Button, Tag, Table, Divider, message, Space, Alert, Spin, Statistic,
    Progress, List, Modal, Grid
} from 'antd';
import {
    CheckCircleOutlined, CreditCardOutlined, SafetyOutlined, RocketOutlined, DownloadOutlined,
    CalendarOutlined, FileTextOutlined, CrownOutlined, UserOutlined, ShoppingCartOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuth } from '../../../aws/AuthContext';

const { useBreakpoint } = Grid;
const { Title, Text } = Typography;

const PRECIO_USUARIO_EXTRA_MES = 25000;

// Catálogo base general (Se ajusta dinámicamente si el cliente tiene un precio pactado)
const CATALOGO_BASE = {
    basico: {
        nombre: 'Plan Básico POS',
        precioMensualDefault: 35000,
        usuariosIncluidos: 2, // 1 CEO + 1 Empleado
        periodos: [
            { key: 'basico1m', meses: 1, titulo: '1 Mes', descripcion: 'Cobro mensual estándar' },
            { key: 'basico3m', meses: 3, titulo: '3 Meses', descripcion: 'Renovación por 90 días' },
            { key: 'basico6m', meses: 6, titulo: '6 Meses', descripcion: 'Renovación semestral' }
        ],
        caracteristicas: [
            'Hasta 2 usuarios activos (1 CEO + 1 Empleado)',
            'Punto de Venta (POS) ilimitado',
            'Gestión de inventario en tiempo real',
            'Acceso seguro en la nube 24/7',
            'Soporte técnico estándar',
            'Catálogo virtual de productos'
        ]
    },
    premium: {
        nombre: 'Plan Premium POS',
        precioMensualDefault: 59000,
        usuariosIncluidos: 6, // 1 CEO + 5 Empleados
        periodos: [
            { key: 'premium1m', meses: 1, titulo: '1 Mes', descripcion: 'Cobro mensual estándar' },
            { key: 'premium3m', meses: 3, titulo: '3 Meses', descripcion: 'Renovación por 90 días' },
            { key: 'premium6m', meses: 6, titulo: '6 Meses', descripcion: 'Renovación semestral' }
        ],
        caracteristicas: [
            'Hasta 6 usuarios activos incluidos',
            'Opción de usuarios adicionales ($25.000/mes c/u)',
            'Múltiples cajas abiertas',
            'App de Auditoría Móvil incluida',
            'Informes financieros detallados',
            'Soporte técnico prioritario'
        ]
    }
};

const SeccionSuscripcion = ({ session, config }) => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { getJwtToken } = useAuth();

    const [planActual, setPlanActual] = useState(session?.empresa?.plan_actual || 'basico');
    const [precioPactado, setPrecioPactado] = useState(null);
    const [totalUsuariosActivos, setTotalUsuariosActivos] = useState(1);

    const [loadingPay, setLoadingPay] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [historial, setHistorial] = useState([]);

    const [isModalPlanesOpen, setIsModalPlanesOpen] = useState(false);
    const [modoModal, setModoModal] = useState('renovar'); // 'renovar' | 'upgrade'

    const [periodoSeleccionado, setPeriodoSeleccionado] = useState(1); // 1, 3 o 6 meses

    const [modalComprobante, setModalComprobante] = useState({ open: false, record: null });

    const [estadoPlan, setEstadoPlan] = useState({
        fechaVencimiento: '-',
        diasRestantes: 0,
        porcentajeUso: 0,
        activa: true,
        porVencer: false,
        bloqueada: false
    });

    const fetchDatosSuscripcion = async () => {
        setLoadingData(true);
        try {
            const token = await getJwtToken();
            const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/v1-suscripciones?empresa_id=${session?.empresa?.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (data.plan_actual) setPlanActual(data.plan_actual);
            if (data.precio_pactado_mes) setPrecioPactado(data.precio_pactado_mes);
            if (data.total_usuarios_activos) setTotalUsuariosActivos(data.total_usuarios_activos);

            setHistorial(data.historial || []);
            calcularEstadoSuscripcion(data.fecha_vencimiento);
        } catch (error) {
            console.error("Error cargando suscripción:", error);
            message.error("No se pudieron cargar los detalles de la suscripción.");
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        fetchDatosSuscripcion();
        // eslint-disable-next-line
    }, []);

    const calcularEstadoSuscripcion = (fechaVenceDato) => {
        if (!fechaVenceDato) return;
        const hoy = dayjs().startOf('day');
        const vencimiento = dayjs(fechaVenceDato).startOf('day');
        const diffDias = vencimiento.diff(hoy, 'day');
        let porcentaje = Math.max(0, Math.min(100, (diffDias / 30) * 100));

        setEstadoPlan({
            fechaVencimiento: vencimiento.toDate().toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' }),
            diasRestantes: diffDias < 0 ? 0 : diffDias,
            porcentajeUso: porcentaje,
            activa: diffDias >= 0,
            porVencer: diffDias >= 0 && diffDias <= 5,
            bloqueada: diffDias < 0
        });
    };

    // Determinación del plan objetivo en el Modal
    const targetPlanKey = modoModal === 'upgrade' ? 'premium' : planActual;
    const infoPlanObjetivo = CATALOGO_BASE[targetPlanKey] || CATALOGO_BASE.basico;

    // Lógica de cálculo de precios personalizada o base
    const precioBaseMesServicio = precioPactado !== null ? precioPactado : infoPlanObjetivo.precioMensualDefault;

    // Cálculo de usuarios extra sobre la capacidad del plan seleccionado
    const usuariosExcedentes = Math.max(0, totalUsuariosActivos - infoPlanObjetivo.usuariosIncluidos);

    // Liquidación detallada
    const valorSubtotalPlan = precioBaseMesServicio * periodoSeleccionado;
    const valorSubtotalUsuariosExtra = (usuariosExcedentes * PRECIO_USUARIO_EXTRA_MES) * periodoSeleccionado;
    const valorTotalFactura = valorSubtotalPlan + valorSubtotalUsuariosExtra;

    const abrirModal = (modo) => {
        setModoModal(modo);
        setPeriodoSeleccionado(1);
        setIsModalPlanesOpen(true);
    };

    const handlePagarWompi = async () => {
        setLoadingPay(true);

        if (!window.WidgetCheckout) {
            message.error("El procesador de pagos no se encuentra listo. Por favor refresca la página.");
            setLoadingPay(false);
            return;
        }

        const amountInCents = valorTotalFactura * 100;
        const currency = 'COP';
        const reference = `YUGONUB_${targetPlanKey}_${periodoSeleccionado}M_${session?.empresa?.id}_${Date.now()}`;

        try {
            const token = await getJwtToken();
            const { data } = await axios.post(
                `${process.env.REACT_APP_API_URL}/v1-wompi-firma`,
                { reference, amountInCents, currency },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (!data?.signature) {
                throw new Error("No se pudo obtener la firma de seguridad.");
            }

            const checkout = new window.WidgetCheckout({
                currency,
                amountInCents,
                reference,
                publicKey: process.env.REACT_APP_WOMPI_PUB,
                signature: { integrity: data.signature }
            });

            checkout.open((result) => {
                const transaction = result?.transaction;

                if (transaction?.status === 'APPROVED') {
                    message.success(`¡Pago aprobado! Tu suscripción ha sido renovada exitosamente.`);
                    setIsModalPlanesOpen(false);
                    fetchDatosSuscripcion();
                } else if (transaction?.status === 'DECLINED') {
                    message.error("El pago fue rechazado por la entidad bancaria.");
                } else if (transaction?.status === 'VOIDED') {
                    message.warning("La transacción fue cancelada.");
                } else {
                    message.info(`Transacción finalizada con estado: ${transaction?.status || 'Cancelado'}`);
                }
            });

            setLoadingPay(false);

        } catch (error) {
            message.error("Error al iniciar la pasarela de pagos.");
            setLoadingPay(false);
        }
    };

    const columns = [
        { title: 'Fecha de Pago', dataIndex: 'fecha', render: (f) => new Date(f).toLocaleDateString('es-CO') },
        { title: 'Referencia', dataIndex: 'wompi_transaction_id', render: (text) => <Text type="secondary" style={{ fontSize: '12px', fontFamily: 'monospace' }}>{text}</Text>, responsive: ['sm'] },
        { title: 'Medio', dataIndex: 'payment_method', render: (m) => <Tag color="blue">{m?.toUpperCase()}</Tag> },
        { title: 'Monto Pagado', dataIndex: 'monto', render: (m) => <b>${Number(m).toLocaleString('es-CO')} COP</b> },
        { title: 'Estado', dataIndex: 'estado', render: (e) => <Tag color={e === 'APPROVED' ? 'success' : 'error'}>{e}</Tag> },
        {
            title: 'Soporte',
            key: 'action',
            render: (_, record) => record.estado === 'APPROVED' ? (
                <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    size="small"
                    onClick={() => verComprobanteWompi(record)}
                >
                    Ver comprobante
                </Button>
            ) : <Text type="secondary" style={{ fontSize: '12px' }}>N/A</Text>
        }
    ];

    if (loadingData) return <div style={{ textAlign: 'center', padding: '100px 0' }}><Spin size="large" /></div>;

    let colorPrincipal = estadoPlan.bloqueada ? '#f5222d' : (estadoPlan.porVencer ? '#faad14' : '#52c41a');
    let progressStatus = (estadoPlan.bloqueada || estadoPlan.porVencer) ? 'exception' : 'normal';

    const infoPlanActualVisual = CATALOGO_BASE[planActual] || CATALOGO_BASE.basico;
    const precioBaseActual = precioPactado !== null ? precioPactado : infoPlanActualVisual.precioMensualDefault;

    const verComprobanteWompi = (record) => {
        setModalComprobante({ open: true, record });
    };


    return (
        <div className="fade-in" style={{ maxWidth: '1200px', margin: '0 auto' }}>

            {!isMobile && (
                <div style={{ marginBottom: 24 }}>
                    <Title level={3} style={{ margin: 0 }}>Facturación y Suscripción</Title>
                    <Text type="secondary">Consulta los detalles de tu plan activo, tarifas y comprobantes de pago.</Text>
                </div>
            )}

            {estadoPlan.porVencer && <Alert message={<b>¡Atención! Tu plan vencerá pronto</b>} description={`Te quedan ${estadoPlan.diasRestantes} días. Si renuevas hoy, los días restantes se acumularán a tu nuevo periodo.`} type="warning" showIcon style={{ marginBottom: 24, borderRadius: 8 }} />}
            {estadoPlan.bloqueada && <Alert message={<b>Servicio Suspendido</b>} description="Tu periodo de suscripción ha expirado. Renueva para continuar utilizando la plataforma Yugonub." type="error" showIcon style={{ marginBottom: 24, borderRadius: 8 }} />}

            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>

                {/* TARJETA PRINCIPAL DE PLAN ACTIVO */}
                <Col xs={24} lg={16}>
                    <Card style={{ borderRadius: 16, height: '100%', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} bodyStyle={{ padding: 0 }}>
                        <div style={{ height: '6px', width: '100%', backgroundColor: colorPrincipal, borderRadius: '16px 16px 0 0' }}></div>
                        <div style={{ padding: '32px' }}>
                            <Row align="middle" gutter={[32, 32]}>
                                <Col xs={24} sm={12}>
                                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                        <Text type="secondary" style={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '11px', fontWeight: 'bold' }}>Suscripción Activa</Text>
                                        <Title level={2} style={{ margin: 0 }}>{infoPlanActualVisual.nombre}</Title>
                                        <Space wrap style={{ marginTop: '4px' }}>
                                            {estadoPlan.activa && !estadoPlan.porVencer && <Tag color="success">ACTIVO</Tag>}
                                            {estadoPlan.porVencer && <Tag color="warning">POR VENCER</Tag>}
                                            {estadoPlan.bloqueada && <Tag color="error">SUSPENDIDO</Tag>}

                                            {precioPactado !== null && (
                                                <Tag color="gold" icon={<CrownOutlined />}>TARIFA ESPECIAL</Tag>
                                            )}
                                        </Space>

                                        <div style={{ marginTop: 12 }}>
                                            <Text type="secondary" style={{ fontSize: '13px', display: 'block' }}>Base del plan:</Text>
                                            <Text strong style={{ fontSize: '18px', color: '#0f172a' }}>
                                                ${precioBaseActual.toLocaleString('es-CO')} COP <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#64748b' }}>/ mes</span>
                                            </Text>
                                        </div>

                                        <div style={{ background: '#f8fafc', padding: '10px 12px', borderRadius: '8px', marginTop: '8px', border: '1px solid #e2e8f0' }}>
                                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                                <UserOutlined /> Usuarios activos en sistema: <b>{totalUsuariosActivos}</b>
                                            </Text>
                                        </div>
                                    </Space>

                                    <div style={{ marginTop: '24px' }}>
                                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                            <Button type="primary" size="large" icon={<CreditCardOutlined />} onClick={() => abrirModal('renovar')} style={{ width: '100%', height: '48px', borderRadius: '8px', backgroundColor: '#22BABB', borderColor: '#22BABB', fontWeight: 'bold' }}>
                                                {estadoPlan.bloqueada ? "Reactivar Suscripción" : "Renovar Plan Actual"}
                                            </Button>

                                            {planActual === 'basico' && (
                                                <Button block size="large" icon={<CrownOutlined />} onClick={() => abrirModal('upgrade')} style={{ height: '48px', borderRadius: '8px', color: '#d97706', borderColor: '#fcd34d', fontWeight: 'bold', backgroundColor: '#fffbeb' }}>
                                                    Mejorar a Plan Premium
                                                </Button>
                                            )}
                                        </Space>

                                        {/* REINCORPORACIÓN DE LOGOS DE WOMPI Y PCI DSS */}
                                        <div style={{ marginTop: '20px' }}>
                                            <Space align="center" size="middle" style={{ flexWrap: 'wrap' }}>
                                                <img src='/img/Wompi_LogoSecundario.jpg' alt="Wompi Bancolombia" style={{ height: '42px', borderRadius: '4px' }} />
                                                <Divider type="vertical" style={{ height: '28px', backgroundColor: '#e2e8f0' }} />
                                                <img src='/img/PCI.jpg' alt="Seguridad PCI DSS" style={{ height: '45px' }} />
                                            </Space>
                                            <Text type="secondary" style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px', color: '#64748b' }}>
                                                <SafetyOutlined style={{ color: '#52c41a' }} /> Transacción encriptada y segura.
                                            </Text>
                                        </div>
                                    </div>
                                </Col>

                                <Col xs={24} sm={12}>
                                    <Row gutter={[16, 16]} align="middle" justify="center">
                                        <Col style={{ textAlign: 'center' }}>
                                            <Progress type="dashboard" percent={estadoPlan.porcentajeUso} strokeColor={colorPrincipal} status={progressStatus} size={110} format={() => (
                                                <div style={{ fontSize: '22px', fontWeight: 'bold', color: colorPrincipal }}>
                                                    {estadoPlan.diasRestantes} <br />
                                                    <span style={{ fontSize: '11px', color: '#8c8c8c', fontWeight: 'normal' }}>DÍAS RESTANTES</span>
                                                </div>
                                            )} />
                                        </Col>
                                        <Col style={{ textAlign: 'center', width: '100%', marginTop: '12px' }}>
                                            <Statistic title={<><CalendarOutlined /> Próximo Vencimiento</>} value={estadoPlan.fechaVencimiento} valueStyle={{ fontSize: '15px', fontWeight: '600' }} />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </div>
                    </Card>
                </Col>

                {/* DETALLES DE LO QUE INCLUYE */}
                <Col xs={24} lg={8}>
                    <Card title={<><RocketOutlined style={{ color: '#22BABB', marginRight: 8 }} /> Características de tu Plan</>} style={{ borderRadius: 16, height: '100%', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                        <List itemLayout="horizontal" dataSource={infoPlanActualVisual.caracteristicas} renderItem={item => (
                            <List.Item style={{ border: 'none', padding: '6px 0' }}>
                                <List.Item.Meta avatar={<CheckCircleOutlined style={{ color: '#52c41a' }} />} title={<Text style={{ fontWeight: 'normal', fontSize: '13px' }}>{item}</Text>} />
                            </List.Item>
                        )} />
                    </Card>
                </Col>
            </Row>

            {/* TABLA DE HISTORIAL DE PAGOS */}
            <Card title={<><FileTextOutlined style={{ marginRight: 8 }} /> Historial de Transacciones</>} style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <Table dataSource={historial} columns={columns} pagination={{ pageSize: 5 }} size="middle" rowKey="wompi_transaction_id" scroll={{ x: 'max-content' }} locale={{ emptyText: "No hay registros de pago" }} />
            </Card>

            {/* MODAL CON EL RESUMEN Y DESGLOSE EXPLICADO */}
            <Modal
                title={
                    <div style={{ textAlign: 'center', padding: '10px 0 0 0' }}>
                        <Title level={4} style={{ margin: 0 }}>
                            {modoModal === 'upgrade' ? 'Actualizar a Plan Premium' : 'Liquidación de Renovación'}
                        </Title>
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            Selecciona los meses a renovar para ver el desglose detallado de tu pago.
                        </Text>
                    </div>
                }
                open={isModalPlanesOpen}
                onCancel={() => {
                    setIsModalPlanesOpen(false);
                    setLoadingPay(false);
                }}
                footer={null}
                width={650}
                centered
            >
                {/* SELECTOR DE MESES */}
                <div style={{ marginTop: '20px', marginBottom: '24px' }}>
                    <Text strong style={{ display: 'block', marginBottom: '10px', textTransform: 'uppercase', fontSize: '11px', color: '#64748b', letterSpacing: '0.5px' }}>
                        1. Selecciona el periodo de tiempo:
                    </Text>
                    <Row gutter={[12, 12]}>
                        {infoPlanObjetivo.periodos.map((p) => {
                            const isSelected = periodoSeleccionado === p.meses;
                            return (
                                <Col xs={8} key={p.key}>
                                    <div
                                        onClick={() => setPeriodoSeleccionado(p.meses)}
                                        style={{
                                            border: isSelected ? '2px solid #22BABB' : '1px solid #cbd5e1',
                                            borderRadius: '10px',
                                            padding: '12px 8px',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? '#f0fdfa' : '#fff',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <Text strong style={{ display: 'block', fontSize: '15px', color: isSelected ? '#0f766e' : '#1e293b' }}>
                                            {p.titulo}
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: '11px' }}>{p.descripcion}</Text>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                </div>

                {/* RESUMEN DETALLADO DEL COBRO */}
                <Card size="small" style={{ borderRadius: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: '24px' }} bodyStyle={{ padding: '16px 20px' }}>
                    <Text strong style={{ display: 'block', marginBottom: '12px', textTransform: 'uppercase', fontSize: '11px', color: '#64748b', letterSpacing: '0.5px' }}>
                        <ShoppingCartOutlined /> 2. Desglose detallado del cobro:
                    </Text>

                    {/* Fila 1: Costo del Plan */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                        <div>
                            <Text style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>{infoPlanObjetivo.nombre}</Text>
                            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                                ${precioBaseMesServicio.toLocaleString('es-CO')} COP/mes × {periodoSeleccionado} {periodoSeleccionado === 1 ? 'mes' : 'meses'}
                                {precioPactado !== null && <Tag color="gold" style={{ marginLeft: 6, fontSize: '10px' }}>Precio Especial</Tag>}
                            </Text>
                        </div>
                        <Text strong style={{ fontSize: '14px', color: '#0f172a' }}>
                            ${valorSubtotalPlan.toLocaleString('es-CO')} COP
                        </Text>
                    </div>

                    <Divider style={{ margin: '8px 0' }} />

                    {/* Fila 2: Usuarios Incluidos vs Adicionales */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                        <div>
                            <Text style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                                Usuarios del sistema ({totalUsuariosActivos} en uso)
                            </Text>
                            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                                {infoPlanObjetivo.usuariosIncluidos} incluidos en el plan
                                {usuariosExcedentes > 0 ? ` + ${usuariosExcedentes} usuario(s) extra ($25.000/mes c/u)` : ' (Sin excedentes)'}
                            </Text>
                        </div>
                        <Text strong style={{ fontSize: '14px', color: usuariosExcedentes > 0 ? '#86198f' : '#0f172a' }}>
                            {usuariosExcedentes > 0 ? `+ $${valorSubtotalUsuariosExtra.toLocaleString('es-CO')} COP` : '$0 COP'}
                        </Text>
                    </div>

                    <Divider style={{ margin: '12px 0', borderColor: '#cbd5e1' }} />

                    {/* Fila Total */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '4px' }}>
                        <div>
                            <Text strong style={{ fontSize: '16px', color: '#0f172a' }}>TOTAL A PAGAR:</Text>
                            <Text type="secondary" style={{ display: 'block', fontSize: '11px' }}>
                                Cubre el servicio desde hoy por {periodoSeleccionado * 30} días
                            </Text>
                        </div>
                        <Text strong style={{ fontSize: '22px', color: '#22BABB' }}>
                            ${valorTotalFactura.toLocaleString('es-CO')} COP
                        </Text>
                    </div>
                </Card>

                {usuariosExcedentes > 0 && (
                    <Alert
                        message={<Text style={{ fontSize: '12px' }}><InfoCircleOutlined /> Si no deseas pagar por usuarios adicionales, puedes desactivar los usuarios que no utilices desde el módulo <b>Ajustes &gt; Usuarios</b> antes de realizar el pago.</Text>}
                        type="info"
                        showIcon={false}
                        style={{ marginBottom: '20px', borderRadius: '8px' }}
                    />
                )}

                {/* BOTÓN PAGO */}
                <div style={{ textAlign: 'center' }}>
                    <Button
                        type="primary"
                        size="large"
                        icon={<CreditCardOutlined />}
                        loading={loadingPay}
                        onClick={handlePagarWompi}
                        style={{ width: '100%', height: '50px', fontSize: '16px', borderRadius: '10px', backgroundColor: '#22BABB', borderColor: '#22BABB', fontWeight: 'bold' }}
                    >
                        Pagar ${valorTotalFactura.toLocaleString('es-CO')} COP Seguro con Wompi
                    </Button>
                    <Space style={{ marginTop: '12px', justifyContent: 'center' }}>
                        <SafetyOutlined style={{ color: '#52c41a' }} />
                        <Text type="secondary" style={{ fontSize: '11px' }}>Pago procesado y protegido por Wompi Bancolombia</Text>
                    </Space>
                </div>
            </Modal>

            {/* MODAL DE COMPROBANTE DE PAGO FORMAL */}
            <Modal
                open={modalComprobante.open}
                onCancel={() => setModalComprobante({ open: false, record: null })}
                footer={[
                    <Button key="print" type="primary" icon={<DownloadOutlined />} onClick={() => window.print()} style={{ backgroundColor: '#22BABB', borderColor: '#22BABB' }}>
                        Imprimir / Guardar PDF
                    </Button>
                ]}
                width={500}
                centered
            >
                {modalComprobante.record && (
                    <div style={{ padding: '10px 0' }}>
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '12px' }} />
                            <Title level={4} style={{ margin: 0 }}>Comprobante de Pago</Title>
                            <Text type="secondary">Plataforma YUGONUB POS</Text>
                        </div>

                        <Card size="small" style={{ borderRadius: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Referencia Wompi:</Text>
                                    <Text strong style={{ fontFamily: 'monospace', fontSize: '12px' }}>{modalComprobante.record.wompi_transaction_id}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Fecha de Transacción:</Text>
                                    <Text strong>{new Date(modalComprobante.record.fecha).toLocaleString('es-CO')}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Método de Pago:</Text>
                                    <Tag color="blue">{modalComprobante.record.payment_method?.toUpperCase()}</Tag>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Estado:</Text>
                                    <Tag color="success">APROBADO</Tag>
                                </div>
                                <Divider style={{ margin: '8px 0' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text strong style={{ fontSize: '16px' }}>Total Abonado:</Text>
                                    <Text strong style={{ fontSize: '20px', color: '#22BABB' }}>
                                        ${Number(modalComprobante.record.monto).toLocaleString('es-CO')} COP
                                    </Text>
                                </div>
                            </Space>
                        </Card>

                        <div style={{ textAlign: 'center', marginTop: '16px' }}>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                Transacción procesada de forma segura mediante Wompi Bancolombia.
                            </Text>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default SeccionSuscripcion;